import React, { useState } from 'react';

import { ClickAwayListener, IconButton, Tooltip } from '@mui/material';

import { useSortable } from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import ClearIcon from '@mui/icons-material/Clear';

import Box from '@mui/material/Box';

import { Button } from 'components/Button';

import { StyledTextField } from 'components/Input/styles';

import { colors } from 'theme/colors';
import { useStore } from 'utils/hooks/store';

import { ItemContainer } from './styles';
import { modifyInputValue } from '../../CreateEditFieldModal/types';
import { DropdownSortableItemProps } from './types';

function DropdownSortableItem({
  id,
  editView,
  options,
  setOptions,
  item,
  disabled,
  disableDeactivate,
  clearError,
}: DropdownSortableItemProps): JSX.Element {
  const { notification, formBuilder } = useStore();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled,
  });
  const [name, setName] = useState<string>(item.value);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [editingName, setEditingName] = useState<boolean>(formBuilder.isDropdownOptionInEdit === item.key);
  const handleEditNameOfOption = (value: string): void => {
    setName(value);
  };

  const handleActiveChange = (): void => {
    // if the key do not match the display key and its in edit view, remove it from the options
    if (editView && item.key !== item.displayKey) {
      const newData = options.filter((opt) => opt.key !== item.key);
      setOptions(newData);
      clearError?.();

      return;
    }

    const newData = options
      .map((opt) => (opt.key === item.key ? { ...opt, active: editView ? false : opt.active } : opt))
      .filter((opt) => editView || opt.key !== item.key);
    setOptions(newData);

    clearError?.();
  };

  const handleClickAway = (): void => {
    if (!editingName) return;
    if (name.trim() === '') {
      notification.enqueueErrorSnackbar('Dropdown option cannot be empty. Please enter a value before saving.');
      return;
    }
    setEditingName(false);
    const newData = options.map((opt) =>
      opt.key === item.key
        ? {
            ...opt,
            value: name,
            displayKey: opt.key.startsWith('untitled') || !editView ? modifyInputValue(name) : opt.displayKey,
          }
        : opt
    );

    setOptions(newData);

    clearError?.();
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        background: '#f9f9f9',
        borderRadius: '4px',
        border: `1px solid ${colors.grey[400]}`,
        margin: '4px 0',
      }}
    >
      <div {...attributes} {...listeners} style={{ height: '20px', cursor: disabled ? 'default' : 'grab' }}>
        <DragIndicatorIcon style={{ fill: disabled ? '#D9D9D9' : colors.grey[500], height: '20px' }} />
      </div>
      <ItemContainer>
        <ClickAwayListener touchEvent="onTouchStart" mouseEvent="onMouseDown" onClickAway={handleClickAway}>
          <div key={`${item.key}_name`} style={{ flex: 1 }}>
            {editingName ? (
              <StyledTextField
                autoFocus
                multiline
                fullWidth
                value={name}
                onChange={(e) => handleEditNameOfOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleClickAway();
                  }
                }}
                onFocus={(e) => e.target.select()}
                InputProps={{
                  style: {
                    padding: '0',
                  },
                }}
                sx={{
                  '.MuiInputBase-input': {
                    fontSize: '14px !important',
                  },
                  '.MuiInputBase-root fieldset': {
                    border: 'none !important',
                  },
                }}
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setEditingName(true);
                }}
                style={{ flex: 1, fontSize: '14px', color: disabled ? colors.text.secondary : colors.common.black }}
              >
                {name}
              </div>
            )}
          </div>
        </ClickAwayListener>
        {!editingName && (
          <Tooltip title={item.displayKey.length > 32 ? item.displayKey : ''}>
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: colors.text.disabled,
              }}
            >
              API Name: {item.displayKey}
            </span>
          </Tooltip>
        )}
        {disabled || disableDeactivate ? (
          // empty space for disabled options to keep the layout consistent
          <Box sx={{ flex: 1, height: '30px' }} />
        ) : (
          <>
            {editView ? (
              <Button variant="text" color="primary" size="small" onClick={handleActiveChange}>
                Deactivate
              </Button>
            ) : (
              <IconButton onClick={handleActiveChange}>
                <ClearIcon htmlColor={colors.primary.main} />
              </IconButton>
            )}
          </>
        )}
      </ItemContainer>
    </div>
  );
}

export default DropdownSortableItem;
